import { useEffect, useState, useRef } from 'react';
import {
  Users,
  Plus,
  RefreshCw,
  Trash2,
  ArrowRight,
  AlertCircle,
  X,
  Upload,
  Search,
  ChevronDown,
} from 'lucide-react';

interface TeamMember {
  id: string;
  from_location_id: string;
  from_display_name: string;
  to_location_id: string;
  to_display_name: string;
  created_by_location_id: string | null;
  created_by_admin_id: string | null;
  created_at: string;
}

interface Location {
  location_id: string;
  location_name: string | null; // API returns billing_display_name as location_name
}

interface FormData {
  from_location_id: string;
  to_location_id: string;
}

const emptyFormData: FormData = {
  from_location_id: '',
  to_location_id: '',
};

// Searchable Location Dropdown Component
function LocationSelect({
  locations,
  value,
  onChange,
  placeholder,
  disabled,
  excludeLocationId,
}: {
  locations: Location[];
  value: string;
  onChange: (locationId: string) => void;
  placeholder: string;
  disabled?: boolean;
  excludeLocationId?: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Filter locations based on search term and exclusion
  const filteredLocations = locations.filter((loc) => {
    if (excludeLocationId && loc.location_id === excludeLocationId) return false;
    const searchLower = searchTerm.toLowerCase();
    const displayName = loc.location_name?.toLowerCase() || '';
    const locationId = loc.location_id.toLowerCase();
    return displayName.includes(searchLower) || locationId.includes(searchLower);
  });

  // Get selected location display
  const selectedLocation = locations.find((loc) => loc.location_id === value);
  const displayText = selectedLocation
    ? selectedLocation.location_name || selectedLocation.location_id
    : '';

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus input when opening
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const handleSelect = (locationId: string) => {
    onChange(locationId);
    setIsOpen(false);
    setSearchTerm('');
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Selected value display / trigger */}
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`w-full px-3 py-2 border rounded text-left flex items-center justify-between ${
          disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white hover:border-gray-400'
        } ${isOpen ? 'ring-2 ring-blue-500 border-blue-500' : ''}`}
      >
        <span className={value ? 'text-gray-900' : 'text-gray-400'}>
          {displayText || placeholder}
        </span>
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-64 overflow-hidden">
          {/* Search input */}
          <div className="p-2 border-b">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                ref={inputRef}
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by name or location ID..."
                className="w-full pl-8 pr-3 py-1.5 text-sm border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          {/* Options list */}
          <div className="overflow-y-auto max-h-48">
            {filteredLocations.length === 0 ? (
              <div className="px-3 py-4 text-sm text-gray-500 text-center">
                No locations found
              </div>
            ) : (
              filteredLocations.map((loc) => (
                <button
                  key={loc.location_id}
                  type="button"
                  onClick={() => handleSelect(loc.location_id)}
                  className={`w-full px-3 py-2 text-left hover:bg-blue-50 ${
                    value === loc.location_id ? 'bg-blue-100' : ''
                  }`}
                >
                  <div className="font-medium text-gray-900 text-sm">
                    {loc.location_name || '(No name)'}
                  </div>
                  <div className="text-xs text-gray-500 font-mono">
                    {loc.location_id}
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function TeamMembers() {
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Locations for dropdowns
  const [locations, setLocations] = useState<Location[]>([]);
  const [loadingLocations, setLoadingLocations] = useState(false);

  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState<FormData>(emptyFormData);
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Bulk import state
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkText, setBulkText] = useState('');
  const [bulkError, setBulkError] = useState<string | null>(null);
  const [bulkSaving, setBulkSaving] = useState(false);
  const [bulkResults, setBulkResults] = useState<{
    created: number;
    skipped: number;
    errors: string[];
  } | null>(null);

  // Filter state
  const [filterFrom, setFilterFrom] = useState('');
  const [filterTo, setFilterTo] = useState('');

  useEffect(() => {
    fetchTeamMembers();
    fetchLocations();
  }, []);

  const fetchLocations = async () => {
    setLoadingLocations(true);
    try {
      // Fetch all locations with billing config
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/admin/billing/locations?limit=1000`,
        { credentials: 'include' }
      );

      const data = (await response.json()) as {
        success: boolean;
        locations?: Array<{
          location_id: string;
          location_name: string | null;
        }>;
        error?: string;
      };

      if (data.success && data.locations) {
        // Sort by display name
        const sorted = data.locations.sort((a, b) => {
          const nameA = a.location_name?.toLowerCase() || a.location_id.toLowerCase();
          const nameB = b.location_name?.toLowerCase() || b.location_id.toLowerCase();
          return nameA.localeCompare(nameB);
        });
        setLocations(sorted);
      }
    } catch (err) {
      console.error('[TeamMembers] Error fetching locations:', err);
    } finally {
      setLoadingLocations(false);
    }
  };

  const fetchTeamMembers = async () => {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      if (filterFrom) params.append('from_location_id', filterFrom);
      if (filterTo) params.append('to_location_id', filterTo);

      const url = `${import.meta.env.VITE_API_URL}/admin/billing/team-members${params.toString() ? `?${params.toString()}` : ''}`;
      const response = await fetch(url, {
        credentials: 'include',
      });

      const data = (await response.json()) as { success: boolean; team_members?: TeamMember[]; error?: string };

      if (data.success) {
        setTeamMembers(data.team_members || []);
      } else {
        setError(data.error || 'Failed to fetch team members');
      }
    } catch (err) {
      console.error('[TeamMembers] Error:', err);
      setError('Failed to fetch team members');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setFormData(emptyFormData);
    setFormError(null);
    setShowModal(true);
  };

  const handleDelete = async (member: TeamMember) => {
    if (!confirm(`Remove transfer permission from "${member.from_display_name}" to "${member.to_display_name}"?`)) {
      return;
    }

    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/admin/billing/team-members/${member.id}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      const data = (await response.json()) as { success: boolean; error?: string };

      if (data.success) {
        fetchTeamMembers();
      } else {
        alert(data.error || 'Failed to remove team member');
      }
    } catch (err) {
      console.error('[TeamMembers] Delete error:', err);
      alert('Failed to remove team member');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    if (!formData.from_location_id || !formData.to_location_id) {
      setFormError('Please select both sender and recipient locations');
      return;
    }

    if (formData.from_location_id === formData.to_location_id) {
      setFormError('Sender and recipient cannot be the same location');
      return;
    }

    setSaving(true);

    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/admin/billing/team-members`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData),
      });

      const data = (await response.json()) as { success: boolean; error?: string };

      if (data.success) {
        setShowModal(false);
        fetchTeamMembers();
      } else {
        setFormError(data.error || 'Failed to add team member');
      }
    } catch (err) {
      console.error('[TeamMembers] Submit error:', err);
      setFormError('Failed to add team member');
    } finally {
      setSaving(false);
    }
  };

  const handleBulkImport = async () => {
    setBulkError(null);
    setBulkResults(null);
    setBulkSaving(true);

    try {
      // Parse the bulk text - expecting CSV format: from_location_id,to_location_id
      const lines = bulkText.trim().split('\n').filter(line => line.trim());
      const relationships: Array<{ from_location_id: string; to_location_id: string }> = [];

      for (const line of lines) {
        const parts = line.split(',').map(p => p.trim());
        if (parts.length >= 2) {
          relationships.push({
            from_location_id: parts[0],
            to_location_id: parts[1],
          });
        }
      }

      if (relationships.length === 0) {
        setBulkError('No valid relationships found. Format: from_location_id,to_location_id (one per line)');
        setBulkSaving(false);
        return;
      }

      const response = await fetch(`${import.meta.env.VITE_API_URL}/admin/billing/team-members/bulk`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ relationships }),
      });

      const data = (await response.json()) as {
        success: boolean;
        results?: { created: number; skipped: number; errors: string[] };
        error?: string;
      };

      if (data.success && data.results) {
        setBulkResults(data.results);
        fetchTeamMembers();
      } else {
        setBulkError(data.error || 'Failed to bulk import');
      }
    } catch (err) {
      console.error('[TeamMembers] Bulk import error:', err);
      setBulkError('Failed to bulk import');
    } finally {
      setBulkSaving(false);
    }
  };

  const handleFilter = () => {
    fetchTeamMembers();
  };

  const clearFilters = () => {
    setFilterFrom('');
    setFilterTo('');
    setTimeout(fetchTeamMembers, 0);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Users className="w-8 h-8 text-blue-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Team Members</h1>
            <p className="text-sm text-gray-600">
              Manage fund transfer permissions between locations
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowBulkModal(true)}
            className="flex items-center gap-2 px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
          >
            <Upload className="w-4 h-4" />
            Bulk Import
          </button>
          <button
            onClick={handleCreate}
            disabled={loadingLocations}
            className="flex items-center gap-2 px-4 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
          >
            <Plus className="w-4 h-4" />
            Add Relationship
          </button>
          <button
            onClick={fetchTeamMembers}
            disabled={loading}
            className="p-2 text-gray-600 hover:text-gray-900 disabled:opacity-50"
            title="Refresh"
          >
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border rounded-lg p-4">
        <div className="flex items-end gap-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              From Location ID
            </label>
            <input
              type="text"
              value={filterFrom}
              onChange={(e) => setFilterFrom(e.target.value)}
              placeholder="Filter by sender..."
              className="w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              To Location ID
            </label>
            <input
              type="text"
              value={filterTo}
              onChange={(e) => setFilterTo(e.target.value)}
              placeholder="Filter by recipient..."
              className="w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <button
            onClick={handleFilter}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Filter
          </button>
          <button
            onClick={clearFilters}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
          >
            Clear
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-500" />
          <span className="text-red-700">{error}</span>
        </div>
      )}

      {/* Table */}
      <div className="bg-white border rounded-lg overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                From (Sender)
              </th>
              <th className="px-4 py-3 text-center text-sm font-medium text-gray-700 w-12">
                {/* Arrow */}
              </th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                To (Recipient)
              </th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                Created By
              </th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                Created At
              </th>
              <th className="px-4 py-3 text-right text-sm font-medium text-gray-700">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {loading ? (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                  <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
                  Loading...
                </td>
              </tr>
            ) : teamMembers.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                  No team member relationships found
                </td>
              </tr>
            ) : (
              teamMembers.map((member) => (
                <tr key={member.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div>
                      <div className="font-medium text-gray-900">
                        {member.from_display_name}
                      </div>
                      <div className="text-xs text-gray-500 font-mono">
                        {member.from_location_id}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <ArrowRight className="w-5 h-5 text-gray-400 mx-auto" />
                  </td>
                  <td className="px-4 py-3">
                    <div>
                      <div className="font-medium text-gray-900">
                        {member.to_display_name}
                      </div>
                      <div className="text-xs text-gray-500 font-mono">
                        {member.to_location_id}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {member.created_by_admin_id ? (
                      <span className="text-blue-600">Admin</span>
                    ) : member.created_by_location_id ? (
                      <span className="text-green-600">Self-service</span>
                    ) : (
                      <span className="text-gray-400">Unknown</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {new Date(member.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => handleDelete(member)}
                      className="p-1 text-red-600 hover:text-red-800"
                      title="Remove"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="text-sm text-gray-500">
        Total: {teamMembers.length} relationship{teamMembers.length !== 1 ? 's' : ''}
        {locations.length > 0 && (
          <span className="ml-4">({locations.length} locations available)</span>
        )}
      </div>

      {/* Add Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-lg font-semibold">Add Transfer Permission</h2>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-4 space-y-4">
              {formError && (
                <div className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-700">
                  {formError}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  From Location (Sender)
                </label>
                <LocationSelect
                  locations={locations}
                  value={formData.from_location_id}
                  onChange={(locationId) =>
                    setFormData({ ...formData, from_location_id: locationId })
                  }
                  placeholder="Select sender location..."
                  disabled={saving || loadingLocations}
                />
              </div>

              <div className="flex justify-center">
                <ArrowRight className="w-6 h-6 text-gray-400" />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  To Location (Recipient)
                </label>
                <LocationSelect
                  locations={locations}
                  value={formData.to_location_id}
                  onChange={(locationId) =>
                    setFormData({ ...formData, to_location_id: locationId })
                  }
                  placeholder="Select recipient location..."
                  disabled={saving || loadingLocations}
                  excludeLocationId={formData.from_location_id}
                />
              </div>

              <p className="text-xs text-gray-500">
                This allows the sender to transfer funds to the recipient.
                One-directional: to allow both ways, create two relationships.
              </p>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving || !formData.from_location_id || !formData.to_location_id}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                >
                  {saving ? 'Adding...' : 'Add Permission'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Bulk Import Modal */}
      {showBulkModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-lg font-semibold">Bulk Import</h2>
              <button
                onClick={() => {
                  setShowBulkModal(false);
                  setBulkText('');
                  setBulkError(null);
                  setBulkResults(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 space-y-4">
              {bulkError && (
                <div className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-700">
                  {bulkError}
                </div>
              )}

              {bulkResults && (
                <div className="bg-green-50 border border-green-200 rounded p-3 text-sm">
                  <div className="text-green-700 font-medium mb-1">Import Complete</div>
                  <div className="text-green-600">
                    Created: {bulkResults.created} | Skipped (duplicates): {bulkResults.skipped}
                  </div>
                  {bulkResults.errors.length > 0 && (
                    <div className="mt-2 text-red-600 text-xs">
                      Errors:
                      <ul className="list-disc list-inside mt-1">
                        {bulkResults.errors.slice(0, 5).map((err, i) => (
                          <li key={i}>{err}</li>
                        ))}
                        {bulkResults.errors.length > 5 && (
                          <li>...and {bulkResults.errors.length - 5} more</li>
                        )}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Relationships (CSV format)
                </label>
                <textarea
                  value={bulkText}
                  onChange={(e) => setBulkText(e.target.value)}
                  placeholder="from_location_id,to_location_id&#10;loc_abc123,loc_def456&#10;loc_ghi789,loc_jkl012"
                  className="w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                  rows={8}
                />
                <p className="text-xs text-gray-500 mt-1">
                  One relationship per line: from_location_id,to_location_id
                </p>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowBulkModal(false);
                    setBulkText('');
                    setBulkError(null);
                    setBulkResults(null);
                  }}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded"
                >
                  Close
                </button>
                <button
                  onClick={handleBulkImport}
                  disabled={bulkSaving || !bulkText.trim()}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                >
                  {bulkSaving ? 'Importing...' : 'Import'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
